import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle, Building2 } from 'lucide-react';

export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | success | failed

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    pollStatus(0);
  }, [sessionId]); // eslint-disable-line

  const pollStatus = async (attempt) => {
    if (attempt >= 6) {
      setStatus('failed');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/billing/checkout-status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user with new subscription status
        const meRes = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(meRes.data);
        // Auto-redirect to organizer after 2 seconds
        setTimeout(() => navigate('/organizer'), 2500);
      } else if (res.data.status === 'expired') {
        setStatus('failed');
      } else {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex flex-col items-center justify-center px-4">
      {/* Logo */}
      <div className="flex items-center gap-2 mb-12">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <span className="font-bold text-xl text-gray-900">Expogrid.io</span>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-10 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-14 h-14 text-teal-500 animate-spin mx-auto mb-5" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Doğrulanıyor</h2>
            <p className="text-gray-500">Lütfen bekleyin, ödemeniz kontrol ediliyor...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-10 h-10 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Başarılı!</h2>
            <p className="text-gray-500 mb-4">
              Ücretli aboneliğiniz aktif edildi. Dashboard'a yönlendiriliyorsunuz...
            </p>
            <div className="flex items-center justify-center gap-2 text-teal-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Yönlendiriliyor...</span>
            </div>
            <button
              onClick={() => navigate('/organizer')}
              className="w-full mt-5 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-xl hover:from-teal-600 hover:to-emerald-700 transition-all"
              data-testid="goto-dashboard-btn"
            >
              Dashboard'a Git
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Doğrulanamadı</h2>
            <p className="text-gray-500 mb-8">
              Ödeme işleminiz doğrulanamadı veya iptal edildi. Lütfen tekrar deneyin.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/pricing')}
                className="flex-1 py-3 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 transition-all"
              >
                Tekrar Dene
              </button>
              <button
                onClick={() => navigate('/organizer')}
                className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
              >
                Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
